import { oc } from "@orpc/contract";
import { z } from "zod";
import { FileStreamSchema } from "../../schemas/common";

export const DivisionOptionSchema = z.object({
  code: z.string(),
  label: z.string(),
});

export const GetReportDivisionsResponseSchema = z.object({
  divisions: z.array(DivisionOptionSchema),
});

export const AdminReportType = z.enum(["staged", "staged_internal"]);

/** How the staged / internal-staged reports split their output. */
export const ReportFileGrouping = z.enum(["single", "per_division"]);

/**
 * The inclusive `startDate <= endDate` check, shared by every report input that
 * takes a window.
 *
 * Five inputs enforced this with five identical copies of the predicate and its
 * message. One copy means a report cannot end up with subtly different wording
 * for the same rejection.
 */
function startNotAfterEnd({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): boolean {
  return startDate <= endDate;
}

const START_NOT_AFTER_END_ISSUE = {
  message: "startDate must not be after endDate",
  // Reported against `endDate`: the user picks the start first, so the end is
  // the field they can fix without redoing the selection.
  path: ["endDate"],
};

/**
 * The filters a date-range-scoped, supplier-filterable S&D report takes.
 *
 * Spread into each report's own `z.object` rather than shared by `.extend()` so
 * a report can still add its own fields; what it buys is that adding a filter
 * here reaches every report that spreads it, instead of the author having to
 * remember which of the three to update — the same reason the 849 list and its
 * Excel export share one filter schema.
 */
/** Date range + division + supplier filters shared by the range reports. */
const rangeFilterFields = {
  startDate: z.string().date(),
  endDate: z.string().date(),
  /** Division codes to include. Empty = all divisions. */
  divisions: z.array(z.string()).default([]),
  /** Optional supplier short name / trading-partner code (ILIKE match). */
  supplier: z.string().optional(),
};

const rangeReportInputFields = {
  ...rangeFilterFields,
  /** "single" = one workbook (divisions on separate tabs); "per_division" = one file each. */
  fileGrouping: ReportFileGrouping.default("single"),
};

export const DownloadTransactionsReportInputSchema = z
  .object({
    reportType: AdminReportType.default("staged"),
    startDate: z.string().date(),
    endDate: z.string().date(),
    divisions: z.array(z.string()).min(1),
    fileGrouping: ReportFileGrouping.default("per_division"),
  })
  .refine(startNotAfterEnd, START_NOT_AFTER_END_ISSUE);

const ReportFileSchema = z.object({
  excel: z.string(),
  filename: z.string(),
  divisionCode: z.string(),
  count: z.number(),
});

/**
 * Base64 file payloads. Still the shape for the reports that have not moved to
 * a streamed download yet (see the base64-envelope follow-up issue).
 */
export const DownloadTransactionsReportResponseSchema = z.object({
  files: z.array(ReportFileSchema),
});

/**
 * Whether the staged / internal-staged report would produce anything, asked
 * before the download starts.
 *
 * The download is a byte stream triggered as a browser navigation, so an empty
 * range can't be reported in its place — anything the server returned instead
 * of a file would land in a new tab. Same reason `as400ExportHasRows` exists
 * next to the item-catalog export.
 */
export const TransactionsReportHasRowsResponseSchema = z.object({
  hasRows: z.boolean(),
  /** Rows the report would cover, for the "exporting N transactions" toast. */
  count: z.number().int().nonnegative(),
});

export const DownloadClaimResponseReportInputSchema = z
  .object({ ...rangeReportInputFields })
  .refine(startNotAfterEnd, START_NOT_AFTER_END_ISSUE);

/**
 * 849 Rejections by Code (SD-97): every rejection an EDI 849 gave a sent claim
 * line, by supplier and rejection code. The date range is the claim line's
 * invoice date, like the other range reports.
 */
export const DownloadRejectionsByCodeReportInputSchema = z
  .object({ ...rangeFilterFields })
  .refine(startNotAfterEnd, START_NOT_AFTER_END_ISSUE);

export type DownloadRejectionsByCodeReportInput = z.infer<
  typeof DownloadRejectionsByCodeReportInputSchema
>;

export const RejectionsByCodeReportHasRowsResponseSchema = z.object({
  hasRows: z.boolean(),
});

/** Transaction statuses selectable in the GL Posting report. */
export const GlPostingReportStatus = z.enum(["staged", "posted", "sent"]);

/**
 * GL Posting report — generates the journal-entry workbook (the same one the
 * Staged page produces) for transactions staged within a date range and in the
 * selected statuses. The date range matches each row's staged date (when we
 * processed/staged it) — NOT when the source transaction first arrived.
 *
 * Side effect: downloading also POSTS any selected `staged` rows (status
 * `staged` → `posted`), freezing them so no user can edit, unstage, or
 * mark-as-no-claim them. `sent` and already-`posted` rows are left untouched.
 * Staged rows with no GL configured can't produce a JE line, so they stay
 * staged and are surfaced as warnings (consistent with "Post All Staged").
 * When `staged` is not among the selected `statuses`, nothing is posted — the
 * download is a read-only re-generation of an existing posting.
 *
 * The optional `minInvoiceDate` / `maxInvoiceDate` window narrows the selection
 * further, by each row's own **invoice date**. This exists because `stagedAt` is
 * stamped once per nightly import run, so a single staged date routinely covers
 * rows invoiced on either side of a month boundary (e.g. Aug 31 and Sep 1) and
 * cannot separate them. Since the same selection drives both the workbook and
 * the posting side effect, the window scopes what gets frozen too — so a
 * month-end posting can take August without freezing September. Omitting both
 * ends leaves the selection exactly as it was before this filter existed.
 *
 * The `postTransactions` flag (admin-controlled toggle) gates the posting side
 * effect entirely: when `false`, the workbook is generated and returned WITHOUT
 * changing any transaction statuses (`postedCount` is always 0). When `true`
 * (default, preserves prior behavior), selected `staged` rows are posted/frozen.
 */
export const DownloadGlPostingReportInputSchema = z
  .object({
    startDate: z.string().date(),
    endDate: z.string().date(),
    divisions: z.array(z.string()).min(1),
    /** Statuses to include. Defaults to all three (preserves prior behavior). */
    statuses: z
      .array(GlPostingReportStatus)
      .min(1)
      .default(["staged", "posted", "sent"]),
    /** "single" = one workbook (divisions on separate tabs); "per_division" = one file each. */
    fileGrouping: ReportFileGrouping.default("single"),
    /**
     * When true, posts selected `staged` rows (→ `posted`, frozen) as a side
     * effect of the download. When false (default), downloads the report only
     * and leaves all transaction statuses untouched.
     *
     * Defaults to FALSE so a plain download can never modify data. It defaulted
     * to `true` until 2026-09-08, which made the safe behaviour depend on every
     * caller remembering to send the field — the frontend did, so the UI was
     * only incidentally safe. Operators re-run this report routinely (the
     * workbook goes to finance for JE reporting, and the same range is pulled
     * more than once), and a download that freezes rows is not re-runnable: the
     * posting flips `staged` → `posted`, and a second run narrowed to `staged`
     * then matches nothing and returns zero files.
     */
    postTransactions: z.boolean().default(false),
    /**
     * Optional inclusive lower bound on each row's own invoice date. Independent
     * of the staged-date range above.
     */
    minInvoiceDate: z.string().date().optional(),
    /** Optional inclusive upper bound on each row's own invoice date. */
    maxInvoiceDate: z.string().date().optional(),
  })
  .refine(startNotAfterEnd, START_NOT_AFTER_END_ISSUE)
  .refine(
    ({ minInvoiceDate, maxInvoiceDate }) =>
      !minInvoiceDate || !maxInvoiceDate || minInvoiceDate <= maxInvoiceDate,
    {
      message: "minInvoiceDate must not be after maxInvoiceDate",
      path: ["maxInvoiceDate"],
    },
  );

const GlPostingFileSchema = z.object({
  excel: z.string(),
  filename: z.string(),
  divisionKey: z.string(),
});

const GlPostingWarningSchema = z.object({
  divisionKey: z.string(),
  message: z.string(),
  /**
   * How the UI should surface this. `error` means real dollars were NOT booked
   * because of a configuration gap (e.g. a branch has no 849 Rejected GL
   * account), so the workbook is incomplete and the operator must act — those
   * get a persistent error surface rather than a transient warning toast.
   * Defaults to `warning` so callers that don't classify keep today's behavior.
   */
  severity: z.enum(["warning", "error"]).default("warning"),
});

export const DownloadGlPostingReportResponseSchema = z.object({
  files: z.array(GlPostingFileSchema),
  warnings: z.array(GlPostingWarningSchema),
  /** Number of staged rows that were posted (frozen) by this download. */
  postedCount: z.number(),
});

/**
 * GL Posting report for EDI 849 responses. For every 844 claim line we got an
 * EDI 849 back for (in the selected date range / divisions), it books the
 * supplier's decision against per-branch accounts: it reverses the original
 * claim (DR 4014-00 / CR 2137), then re-applies per the 849 (DR 2137 approved,
 * CR 4014-00 the ORIGINAL claim, DR 4014-05 the difference between the
 * original claim and what the supplier approved — booked even when the 849
 * status is "accepted" but the credited amount differs). Read-only — no
 * posting side effect. Filtered by the 849 response received date.
 */
export const DownloadGl849PostingReportInputSchema = z
  .object({
    ...rangeReportInputFields,
    /**
     * Detail-only "rejection report": only the Detail tab(s), covering every
     * matched 844/849 line in the period (including lines whose branch has no
     * GL configured), with no JE / Skipped / Unmatched / sample tabs.
     */
    detailOnly: z.boolean().default(false),
    /**
     * Include 849 responses that did NOT arrive over EDI (`source_type` `email`
     * or `manual`) in every query behind this report.
     *
     * Defaults to FALSE — EDI-only, which reproduces the figures this report has
     * always produced, so any difference an operator sees is attributable to
     * having turned this on and to nothing else.
     *
     * This is a data-TRUST boundary, not a design one: non-EDI 849 ingestion is
     * not yet complete enough to post GL from, so widening the set is an
     * explicit operator opt-in rather than a new baseline. Expect the default to
     * flip once email-849 ingestion is trusted — that is a business call, not a
     * cleanup.
     *
     * Deliberately on THIS input only, not on the shared `rangeReportInputFields`:
     * the other range reports were not asked for it, and putting it on the shared
     * fields would silently widen them too.
     */
    includeNonEdi849: z.boolean().default(false),
  })
  .refine(startNotAfterEnd, START_NOT_AFTER_END_ISSUE);

/**
 * The first day we have any Ship & Debit 849 history for.
 *
 * A literal string, deliberately not a formatted `Date`: rendering a
 * UTC-midnight instant through a local date formatter lands on the previous
 * day, which has already caused a cutoff to be quoted to operators a day early
 * (see `GL_849_REJECTED_ACCOUNTS_LIVE_FROM_LABEL`). A calendar boundary has no
 * timezone to convert.
 *
 * Enforced by the contract below AND used as the `min` on the frontend's date
 * picker, so an earlier date is neither pickable nor accepted. It is a guard
 * against a confusing empty result rather than a correctness rule — before this
 * date there is nothing to report on.
 */
export const SD_849_HISTORY_START = "2026-05-01";

/**
 * The sentinel `divisionKey` every previously-decided report warning carries.
 *
 * NOT a division. The backend stamps it (`reportLevelWarnings`) and the frontend
 * keys its persistent banner off it, so the two must agree — which is exactly
 * why it lives here rather than as a literal at each end.
 */
export const PREVIOUSLY_DECIDED_WARNING_KEY = "previously-decided";

/**
 * "Previously Decided 849 Lines" — the standalone report finance runs over a
 * period to work the lines a supplier decided more than once, where an earlier
 * posted period left the difference stranded in a rejected GL account.
 *
 * The same COLUMNS as the `Previously decided` tab inside the GL posting
 * workbook — GL accounts, VDM and invoice date alongside each line — on their
 * own. Not the same row SET: the pairing differs, see below. Read-only —
 * nothing is posted or frozen.
 *
 * **One row per re-decision whose LATER decision falls inside the range**
 * (`queryPreviouslyDecidedTransitions`), so a line re-decided three times
 * reports two rows and a re-decision that happened inside the range is
 * reported. That is NOT the workbook tab's own pairing — the tab pairs against
 * the newest decision strictly before `startDate`, because that is what a
 * monthly JE run would have booked, and its rows post a real journal entry.
 * Report and tab therefore agree on a monthly range and diverge on a wide one,
 * by design: the wide range is the worklist.
 *
 * Deliberately NOT floored at {@link SD_849_HISTORY_START}. Under the old
 * pairing a range starting there was structurally empty — that guard existed to
 * spare operators a blank workbook, and it now blocks the single most useful
 * range instead.
 */
export const DownloadPreviouslyDecidedReportInputSchema = z
  .object({ ...rangeReportInputFields })
  .refine(startNotAfterEnd, START_NOT_AFTER_END_ISSUE);

export type DownloadPreviouslyDecidedReportInput = z.infer<
  typeof DownloadPreviouslyDecidedReportInputSchema
>;

/**
 * One generated workbook, as recorded on the finished job.
 *
 * Carries a `storageKey` rather than the bytes: these run to megabytes and the
 * job payload is a JSONB column that the browser re-reads on every poll. Fetch
 * the content with `getGl849PostingReportFile`.
 */
const Gl849PostingReportFileSchema = z.object({
  filename: z.string(),
  divisionKey: z.string(),
  blockCount: z.number(),
  storageKey: z.string(),
  sizeBytes: z.number(),
});

/**
 * What a finished 849 GL posting run stores on its background job under
 * `payload.result`. The frontend parses the payload with this after
 * `waitForBackgroundJob` settles.
 */
export const Gl849PostingReportJobResultSchema = z.object({
  files: z.array(Gl849PostingReportFileSchema),
  warnings: z.array(GlPostingWarningSchema),
  /**
   * Who the workbooks were emailed to, empty when they weren't. A run only
   * emails when it took long enough that nobody can be assumed to still be
   * watching the tab — see `shouldEmailReport`.
   */
  emailedTo: z.array(z.string()).default([]),
  /** Wall-clock milliseconds the run took, for the UI's completion message. */
  elapsedMs: z.number(),
});

/**
 * Starting the report only hands back the job to watch.
 *
 * The run itself moved to the Temporal worker: its query drives off the entire
 * SENT `sd_e844d` history (the date range bounds only the 849 side), so a
 * wide-scope run is tens of minutes — well past the browser's ~300s fetch
 * limit. Held on the request, those runs left a spinner that could never
 * resolve. The caller polls `core.backgroundJobs.get` and reads
 * `Gl849PostingReportJobResultSchema` off the settled payload.
 */
export const StartGl849PostingReportResponseSchema = z.object({
  backgroundJobId: z.string(),
});

/** Fetch one generated workbook's bytes back off blob storage. */
export const GetGl849PostingReportFileInputSchema = z.object({
  backgroundJobId: z.string(),
  /**
   * Must be one of the keys the job itself recorded — the handler re-reads the
   * job and rejects anything else, so this can't be used to read arbitrary
   * blobs.
   */
  storageKey: z.string(),
});

export const GetGl849PostingReportFileResponseSchema = z.object({
  /** Base64 workbook, same shape the synchronous report used to return. */
  excel: z.string(),
  filename: z.string(),
});

export type DownloadGl849PostingReportInput = z.infer<
  typeof DownloadGl849PostingReportInputSchema
>;

export type Gl849PostingReportJobResult = z.infer<
  typeof Gl849PostingReportJobResultSchema
>;

export type DownloadTransactionsReportInput = z.infer<
  typeof DownloadTransactionsReportInputSchema
>;

/**
 * Whether the 844-claim + 849-response report would produce anything.
 *
 * Asked before the download starts, for the same reason as
 * `transactionsReportHasRows`: the download is a byte stream, so once its
 * headers are out an empty range can only be an empty file.
 */
export const ClaimResponseReportHasRowsResponseSchema = z.object({
  hasRows: z.boolean(),
});

export type DownloadClaimResponseReportInput = z.infer<
  typeof DownloadClaimResponseReportInputSchema
>;

export const sdAdminReportsContracts = {
  getDivisions: oc.output(GetReportDivisionsResponseSchema).route({
    method: "GET",
    summary: "List divisions for ship and debit reports",
    description:
      "Returns the division codes and labels available as filters on the ship and debit admin reports.",
  }),
  // Streamed: one XLSX for `single`, a zip of per-division workbooks otherwise.
  // GET so it can be triggered as a browser navigation — a month-scale report
  // is far too big to hand back as base64 inside a JSON response.
  downloadTransactionsReport: oc
    .input(DownloadTransactionsReportInputSchema)
    .output(FileStreamSchema)
    .route({
      method: "GET",
      summary:
        "Stream the staged / internal-staged transactions report as an XLSX or a per-division zip",
      description:
        "Downloads staged or internally staged claim transactions for a date range and divisions, as one workbook or one file per division. Read-only.",
    }),
  transactionsReportHasRows: oc
    .input(DownloadTransactionsReportInputSchema)
    .output(TransactionsReportHasRowsResponseSchema)
    .route({
      method: "GET",
      summary: "Check if the transactions report has rows",
      description:
        "Counts the staged or internally staged transactions a transactions report would include for the given date range and divisions, before downloading.",
    }),
  // Streamed: one XLSX for `single`, a zip of per-division workbooks otherwise.
  // GET so it can be triggered as a browser navigation. Read-only, so a GET is
  // safe here — unlike the GL posting reports, which post as they download.
  downloadClaimResponseReport: oc
    .input(DownloadClaimResponseReportInputSchema)
    .output(FileStreamSchema)
    .route({
      method: "GET",
      summary:
        "Stream the 844-claim + 849-response sample report as an XLSX or a per-division zip",
      description:
        "Downloads sent claim lines alongside supplier claim responses for a date range, filterable by division and supplier. Read-only.",
    }),
  claimResponseReportHasRows: oc
    .input(DownloadClaimResponseReportInputSchema)
    .output(ClaimResponseReportHasRowsResponseSchema)
    .route({
      method: "GET",
      summary: "Check if the claim response report has rows",
      description:
        "Reports whether the claim and claim-response report would contain any rows for the given date range, divisions and supplier.",
    }),
  // Streamed XLSX (Summary + Detail + Export info). GET so it can be triggered
  // as a browser navigation; read-only.
  downloadRejectionsByCodeReport: oc
    .input(DownloadRejectionsByCodeReportInputSchema)
    .output(FileStreamSchema)
    .route({
      method: "GET",
      summary: "Stream the 849 rejections by code report as an XLSX",
      description:
        "Downloads every rejection an EDI 849 gave a sent claim line, with the rejection code, its meaning and the line's current answer, summarized by supplier and code. Filterable by invoice date, division and supplier. Read-only.",
    }),
  rejectionsByCodeReportHasRows: oc
    .input(DownloadRejectionsByCodeReportInputSchema)
    .output(RejectionsByCodeReportHasRowsResponseSchema)
    .route({
      method: "GET",
      summary: "Check if the 849 rejections by code report has rows",
      description:
        "Reports whether the 849 rejections by code report would contain any rows for the given date range, divisions and supplier.",
    }),
  downloadGlPostingReport: oc
    .input(DownloadGlPostingReportInputSchema)
    .output(DownloadGlPostingReportResponseSchema)
    .route({
      method: "POST",
      summary: "Download the GL posting journal entry report",
      description:
        "Builds journal-entry workbooks for claim transactions staged in a date range. With postTransactions, also posts and freezes the selected staged rows.",
    }),
  /**
   * Start a 849 GL posting run on the worker. Returns the background job to
   * poll — the workbooks are fetched afterwards with
   * `getGl849PostingReportFile`.
   */
  downloadGl849PostingReport: oc
    .input(DownloadGl849PostingReportInputSchema)
    .output(StartGl849PostingReportResponseSchema)
    .route({
      method: "POST",
      summary: "Start the claim response GL posting report",
      description:
        "Starts a background job building journal entries for supplier claim decisions in a date range; returns the job id. Read-only; may email the workbooks when slow.",
    }),
  /**
   * Start a "Previously Decided 849 Lines" run on the worker. Shares the 849 GL
   * report's job kind and blob storage, so the workbooks come back through
   * `getGl849PostingReportFile` like any other 849 report.
   */
  downloadPreviouslyDecidedReport: oc
    .input(DownloadPreviouslyDecidedReportInputSchema)
    .output(StartGl849PostingReportResponseSchema)
    .route({
      method: "POST",
      summary: "Start the previously decided claim lines report",
      description:
        "Starts a background job listing claim lines a supplier re-decided within a date range, with GL accounts; returns the job id. Read-only.",
    }),
  getGl849PostingReportFile: oc
    .input(GetGl849PostingReportFileInputSchema)
    .output(GetGl849PostingReportFileResponseSchema)
    .route({
      method: "POST",
      summary: "Download a generated claim response GL workbook",
      description:
        "Fetches one workbook produced by a finished claim-response GL or previously-decided report job, returned as base64 Excel.",
    }),
} as const;
